import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { bundlesApi } from "@/features/bundles/api/bundlesApi";
import { Card, CardContent, Badge, Button, Skeleton } from "@learn-me/ui";
import { formatCurrency, COURSE_LEVEL_LABELS } from "@learn-me/utils";
import {
  Package,
  BookOpen,
  ArrowLeft,
  Clock,
  Users,
  CheckCircle,
  ShoppingCart,
  Percent,
  GraduationCap,
} from "lucide-react";
import { FadeIn } from "@/components/animated";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function BundleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const {
    data: bundle,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["bundle", slug],
    queryFn: () => bundlesApi.getBySlug(slug!),
    enabled: !!slug,
  });

  const handleBuyBundle = () => {
    if (!bundle) return;

    if (!isAuthenticated) {
      navigate("/login", {
        state: { redirectTo: `/checkout/bundle/${bundle.id}` },
      });
      return;
    }
    navigate(`/checkout/bundle/${bundle.id}`);
  };

  if (isLoading) {
    return (
      <div className="container py-12">
        <Skeleton className="h-8 w-32 mb-8" />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Skeleton className="h-64 w-full mb-6" />
            <Skeleton className="h-10 w-2/3 mb-4" />
            <Skeleton className="h-24 w-full" />
          </div>
          <div>
            <Skeleton className="h-80 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="container py-12 text-center">
        <Package className="h-16 w-16 text-secondary-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Paquete no encontrado</h1>
        <p className="text-secondary-500 mb-6">
          El paquete que buscas no existe o ya no está disponible.
        </p>
        <Link to="/">
          <Button>Volver al inicio</Button>
        </Link>
      </div>
    );
  }

  const totalValue =
    bundle.courses?.reduce((acc, course) => {
      return acc + (course.salePrice || 0);
    }, 0) || 0;

  const savings =
    bundle.hasDiscount && bundle.originalPrice
      ? bundle.originalPrice - (bundle.price || 0)
      : totalValue - (bundle.price || 0);

  return (
    <div className="bg-secondary-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-12">
        <div className="container">
          <FadeIn>
            <Link
              to="/"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al inicio
            </Link>

            <div className="grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <Package className="h-8 w-8" />
                  <span className="text-primary-200 font-medium">
                    Paquete de Cursos
                  </span>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold mb-4">
                  {bundle.name}
                </h1>
                {bundle.shortDescription && (
                  <p className="text-lg text-white/80 mb-6">
                    {bundle.shortDescription}
                  </p>
                )}
                <div className="flex flex-wrap gap-4">
                  <Badge className="bg-white/20 text-white border-0 py-1.5 px-3">
                    <BookOpen className="h-4 w-4 mr-2" />
                    {bundle.courses?.length || 0} cursos incluidos
                  </Badge>
                  {bundle.hasDiscount && (
                    <Badge className="bg-green-500 text-white border-0 py-1.5 px-3">
                      <Percent className="h-4 w-4 mr-2" />
                      ¡Oferta especial!
                    </Badge>
                  )}
                </div>
              </div>

              {/* Card de precio */}
              <Card className="bg-white text-secondary-900 shadow-2xl">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    {bundle.hasDiscount && bundle.originalPrice ? (
                      <>
                        <p className="text-sm text-secondary-400 line-through">
                          {formatCurrency(
                            bundle.originalPrice,
                            bundle.currency,
                          )}
                        </p>
                        <p className="text-4xl font-bold text-green-600">
                          {formatCurrency(bundle.price || 0, bundle.currency)}
                        </p>
                        <p className="text-sm text-green-600 font-medium mt-1">
                          ¡Ahorras {formatCurrency(savings, bundle.currency)}!
                        </p>
                      </>
                    ) : bundle.price ? (
                      <>
                        <p className="text-4xl font-bold text-primary-600">
                          {formatCurrency(bundle.price, bundle.currency)}
                        </p>
                        {savings > 0 && (
                          <p className="text-sm text-secondary-500 mt-1">
                            Valor individual:{" "}
                            {formatCurrency(totalValue, bundle.currency)}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-2xl font-semibold">Consultar precio</p>
                    )}
                  </div>

                  <Button
                    className="w-full mb-4"
                    size="lg"
                    onClick={handleBuyBundle}
                    disabled={!bundle.price}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Comprar Paquete
                  </Button>

                  <ul className="space-y-3 text-sm">
                    <li className="flex items-center gap-2 text-secondary-600">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Acceso inmediato a todos los cursos
                    </li>
                    <li className="flex items-center gap-2 text-secondary-600">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Certificados de finalización
                    </li>
                    <li className="flex items-center gap-2 text-secondary-600">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Actualizaciones gratuitas
                    </li>
                    <li className="flex items-center gap-2 text-secondary-600">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Soporte prioritario
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Contenido */}
      <div className="container py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Descripción */}
            {bundle.description && (
              <FadeIn>
                <Card className="mb-8">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">
                      Descripción del Paquete
                    </h2>
                    <div className="prose prose-secondary max-w-none">
                      <p className="text-secondary-600 whitespace-pre-line">
                        {bundle.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>
            )}

            {/* Lista de cursos */}
            <FadeIn delay={0.1}>
              <h2 className="text-xl font-semibold mb-4">
                Cursos incluidos ({bundle.courses?.length || 0})
              </h2>
              <div className="space-y-4">
                {bundle.courses?.map((course, index) => (
                  <Link key={course.id} to={`/courses/${course.slug}`}>
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 flex gap-4">
                        <div className="flex-shrink-0 w-32 h-20 bg-secondary-100 rounded-lg overflow-hidden">
                          {course.thumbnailUrl ? (
                            <img
                              src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                              alt={course.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <GraduationCap className="h-8 w-8 text-secondary-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-primary-600">
                              Curso {index + 1}
                            </span>
                            {course.isEnrolled && (
                              <Badge variant="success" className="text-xs">
                                Inscrito
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-secondary-900 mb-1 truncate">
                            {course.title}
                          </h3>
                          {course.shortDescription && (
                            <p className="text-sm text-secondary-500 line-clamp-1">
                              {course.shortDescription}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-secondary-400">
                            {course.duration && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {Math.round(course.duration / 60)}h
                              </span>
                            )}
                            {course.studentsCount !== undefined && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3.5 w-3.5" />
                                {course.studentsCount} estudiantes
                              </span>
                            )}
                            {course.level && (
                              <Badge variant="outline" className="text-xs">
                                {COURSE_LEVEL_LABELS[course.level] ??
                                  course.level}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {!course.isFree && course.salePrice && (
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm text-secondary-400 line-through">
                              {formatCurrency(
                                course.salePrice,
                                course.currency || bundle.currency,
                              )}
                            </p>
                            <p className="text-sm font-medium text-green-600">
                              Incluido
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </FadeIn>
          </div>

          {/* Sidebar sticky */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">Resumen del Paquete</h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex justify-between">
                      <span className="text-secondary-500">Cursos</span>
                      <span className="font-medium">
                        {bundle.courses?.length || 0}
                      </span>
                    </li>
                    {totalValue > 0 && (
                      <li className="flex justify-between">
                        <span className="text-secondary-500">
                          Valor individual
                        </span>
                        <span className="font-medium">
                          {formatCurrency(totalValue, bundle.currency)}
                        </span>
                      </li>
                    )}
                    {savings > 0 && (
                      <li className="flex justify-between text-green-600">
                        <span>Tu ahorro</span>
                        <span className="font-medium">
                          {formatCurrency(savings, bundle.currency)}
                        </span>
                      </li>
                    )}
                    <li className="flex justify-between pt-3 border-t">
                      <span className="font-medium">Precio del paquete</span>
                      <span className="font-bold text-lg text-primary-600">
                        {bundle.price
                          ? formatCurrency(bundle.price, bundle.currency)
                          : "Consultar"}
                      </span>
                    </li>
                  </ul>
                  <Button
                    className="w-full mt-6"
                    onClick={handleBuyBundle}
                    disabled={!bundle.price}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Comprar Ahora
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
